import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  BankLimitProposalLineResponse,
  BankLimitProposalResponse,
} from '@interfaces/bank-limits/responses/bank-limit-update-proposal.response';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { BankLimitUpdateHttpService } from '@services/http/bank-limits/bank-limit-update-http.service';
import { RiskMonitoringHttpService } from '@services/http/risk-monitoring/risk-monitoring-http.service';
import { bankLimitUpdateErrorMessage } from '@services/data/bank-limits/bank-limit-update-error.util';
import { ToastHelperService } from '@src/app/core/services/helpers/toast-helper.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { ModalRfqEmailsComponent } from '@features/dashboard/control/risk-management/monitoring/pages/rfqs/components/modal-rfq-emails/modal-rfq-emails.component';
import { finalize, map, startWith } from 'rxjs';
import {
  amountAboveGlobal,
  buildConfirmPayload,
  decisionText,
  excerptView,
  formatBrl,
  formatIsoDate,
  initialLineValue,
  limitTypeTitle,
  limitChangeView,
  LOW_CONFIDENCE_TEXT,
  REJECT_HINT,
  ProposalLineFormValue,
  reportedForeignAmountText,
  validateProposalConfirmation,
  responderLabel,
  senderWarningText,
  unregisteredSenderConfirmation,
} from './bank-limit-proposal-form.util';

type ProposalLineGroup = FormGroup<{
  apply: FormControl<boolean>;
  amountBrl: FormControl<number | null>;
  validityStartDate: FormControl<string | null>;
  validityEndDate: FormControl<string | null>;
}>;

/** Texto pronto de cada tipo de limite para o template. */
interface ProposalLineView {
  index: number;
  title: string;
  line: BankLimitProposalLineResponse;
  foreignAmount: string | null;
  excerpt: ReturnType<typeof excerptView>;
  applied: string | null;
}

/**
 * "Limites informados pelo {banco}" (card 3787): confere a resposta do banco ao pedido de
 * atualização e confirma (grava os limites no Relacionamento Bancário) ou rejeita. Aberta pelo selo
 * do card de Limites da Home e pelo "Conferir valores" dos Detalhes do RFQ, com
 * `BANKRELATIONSHIP_CONFIRM_LIMIT_UPDATE`.
 *
 * Os valores chegam prontos (`GET bank-limit-updates/proposals/{id}`, BRL já convertido); a tela só
 * valida o que foi digitado, com as mesmas frases do 422, e mostra o 422 do backend no topo sem
 * fechar. Proposta já decidida abre só para leitura.
 */
@Component({
  selector: 'app-modal-bank-limit-proposal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule, ModalRfqEmailsComponent],
  templateUrl: './modal-bank-limit-proposal.component.html',
  styleUrls: ['./modal-bank-limit-proposal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalBankLimitProposalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;
  @ViewChild('modalEmails') modalEmails: ModalRfqEmailsComponent | null = null;
  @ViewChild('rejectPanel') rejectPanel?: ElementRef<HTMLElement>;

  /** A proposta foi confirmada ou rejeitada: quem abriu relê os dados. */
  @Output() decided = new EventEmitter<BankLimitProposalResponse>();

  readonly dialogKey = 'bank-limit-proposal';
  readonly lowConfidenceText = LOW_CONFIDENCE_TEXT;
  readonly rejectHint = REJECT_HINT;

  private readonly http = inject(BankLimitUpdateHttpService);
  private readonly rfqHttp = inject(RiskMonitoringHttpService);
  private readonly dateFormatter = inject(DateFormatterService);
  private readonly toast = inject(ToastHelperService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly proposal = signal<BankLimitProposalResponse | null>(null);
  readonly loading = signal(false);
  readonly loadFailed = signal(false);
  readonly submitting = signal(false);
  readonly rejecting = signal(false);
  readonly openingEmail = signal(false);
  /** Passo extra do remetente não cadastrado aberto: o clique de novo no Confirmar não abre outro. */
  private confirmingSender = false;
  /** Mensagem do backend (422) ou da validação no topo da modal. */
  readonly topError = signal<string | null>(null);
  readonly showRejectReason = signal(false);

  private proposalId: string | null = null;

  readonly linesArray = new FormArray<ProposalLineGroup>([]);
  readonly raiseGlobalLimit = new FormControl(false, { nonNullable: true });
  readonly rejectReason = new FormControl<string | null>(null);

  /** Valores do formulário como signal (validação e aviso do global reagem à digitação). */
  private readonly formLines = toSignal(
    this.linesArray.valueChanges.pipe(
      startWith(null),
      map(() => this.readLines())
    ),
    { initialValue: [] as ProposalLineFormValue[] }
  );
  private readonly raiseGlobal = toSignal(this.raiseGlobalLimit.valueChanges, {
    initialValue: false,
  });

  readonly isPending = computed(() => this.proposal()?.status === 'Pending');

  readonly lineViews = computed<ProposalLineView[]>(() =>
    (this.proposal()?.lines ?? []).map((line, index) => ({
      index,
      title: limitTypeTitle(line.limitTypeCode),
      line,
      foreignAmount: reportedForeignAmountText(line),
      excerpt: excerptView(line),
      applied:
        line.appliedAmountBrl !== null
          ? `Gravado: ${formatBrl(line.appliedAmountBrl)} de ${formatIsoDate(line.appliedValidityStartDate)} até ${formatIsoDate(line.appliedValidityEndDate)}`
          : null,
    }))
  );

  /**
   * "Hoje → Novo" de cada tipo (crítica G2), na ordem das linhas: com a proposta pendente usa o que
   * está digitado; decidida, o que foi gravado.
   */
  readonly changeViews = computed(() => {
    const proposal = this.proposal();
    if (!proposal) return [];
    const formLines = this.formLines();
    const pending = this.isPending();
    return proposal.lines.map((line, index) => {
      const next = pending
        ? (formLines[index] ?? { apply: false, amountBrl: null, validityEndDate: null })
        : {
            apply: line.appliedAmountBrl !== null,
            amountBrl: line.appliedAmountBrl,
            validityEndDate: line.appliedValidityEndDate,
          };
      return limitChangeView(line.current, next);
    });
  });

  readonly subtitle = computed(() => {
    const proposal = this.proposal();
    if (!proposal) return '';
    const who = responderLabel(proposal);
    const when = this.dateFormatter.formatDateTimeMinutesInstant(proposal.receivedAt);
    return `Resposta de ${who} em ${when} · RFQ #${proposal.rfqSequentialId}`;
  });

  /** Faixa de remetente fora do cadastro do banco (G2). */
  readonly senderWarning = computed(() => {
    const proposal = this.proposal();
    return proposal ? senderWarningText(proposal) : null;
  });

  readonly decision = computed(() => {
    const proposal = this.proposal();
    return proposal
      ? decisionText(proposal, date =>
          this.dateFormatter.formatDateTimeInFormat(date, 'dd/MM/yyyy')
        )
      : null;
  });

  /** Maior valor marcado acima do limite global (mostra a opção de subir o global). */
  readonly aboveGlobalAmount = computed(() =>
    amountAboveGlobal(this.formLines(), this.proposal()?.creditGlobal)
  );

  readonly raiseGlobalLabel = computed(() => {
    const amount = this.aboveGlobalAmount();
    return amount === null
      ? ''
      : `Atualizar também o limite global do ${this.proposal()?.bankName ?? 'banco'} para ${formatBrl(amount)}`;
  });

  readonly globalWarning = computed(() => {
    const proposal = this.proposal();
    if (!proposal || this.aboveGlobalAmount() === null || this.raiseGlobal()) return null;
    return `O valor informado passa do limite global do ${proposal.bankName} (${formatBrl(proposal.creditGlobal)}). Marque a opção para subir o global junto, ou ajuste o valor.`;
  });

  readonly validationErrors = computed(() => {
    const proposal = this.proposal();
    if (!proposal || !this.isPending()) return [];
    return validateProposalConfirmation(this.formLines(), {
      bankName: proposal.bankName,
      creditGlobal: proposal.creditGlobal,
      raiseGlobalLimit: this.raiseGlobal(),
    });
  });

  constructor(translateService: TranslateService) {
    super(translateService);
  }

  open(proposalId: string): void {
    this.proposalId = proposalId;
    this.proposal.set(null);
    this.topError.set(null);
    this.showRejectReason.set(false);
    this.rejectReason.setValue(null);
    this.raiseGlobalLimit.setValue(false);
    this.linesArray.clear();
    this.dialogRef?.open('', '', '', 'Cancelar', '', this.dialogKey, false, true);
    this.load();
  }

  load(): void {
    const proposalId = this.proposalId;
    if (!proposalId) return;
    this.loading.set(true);
    this.loadFailed.set(false);
    this.http
      .getProposal(proposalId)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: proposal => {
          if (!proposal) {
            this.loadFailed.set(true);
            return;
          }
          this.applyProposal(proposal);
        },
        error: () => this.loadFailed.set(true),
      });
  }

  isFormValid(): boolean {
    return this.isPending() && this.validationErrors().length === 0 && !this.submitting();
  }

  setApply(index: number, apply: boolean): void {
    this.linesArray.at(index)?.controls.apply.setValue(apply);
  }

  lineGroup(index: number): ProposalLineGroup {
    return this.linesArray.at(index);
  }

  submitAction(): void {
    const proposal = this.proposal();
    // Confirmar e rejeitar nunca em voo ao mesmo tempo, nem duas confirmações (revisão de segurança, A18).
    if (!proposal || !this.isPending() || this.submitting() || this.rejecting()) return;
    const errors = this.validationErrors();
    if (errors.length) {
      this.topError.set(errors[0]);
      return;
    }

    // Remetente fora do cadastro do banco: um passo de confirmação antes de gravar (crítica de design, G3).
    const senderCheck = unregisteredSenderConfirmation(proposal);
    if (!senderCheck) {
      this.saveConfirmation(proposal);
      return;
    }
    if (this.confirmingSender) return;
    this.confirmingSender = true;
    this.confirmation
      .confirm(
        senderCheck.title,
        senderCheck.description,
        'danger',
        senderCheck.cancelLabel,
        senderCheck.confirmLabel
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(accepted => {
        this.confirmingSender = false;
        if (
          accepted &&
          this.proposal() === proposal &&
          this.isPending() &&
          !this.submitting() &&
          !this.rejecting()
        ) {
          this.saveConfirmation(proposal);
        }
      });
  }

  private saveConfirmation(proposal: BankLimitProposalResponse): void {
    this.submitting.set(true);
    this.topError.set(null);
    this.http
      .confirm(proposal.id, buildConfirmPayload(this.formLines(), this.raiseGlobal()))
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: updated => {
          this.toast.showToastSuccess(
            'Limites atualizados',
            `Limites do ${proposal.bankName} atualizados.`
          );
          this.finish(updated ?? proposal);
        },
        error: error =>
          this.topError.set(
            bankLimitUpdateErrorMessage(
              error,
              'Não conseguimos gravar os limites agora. Confira a conexão e clique em Confirmar de novo.'
            )
          ),
      });
  }

  /** Entra no modo rejeitar: o motivo aparece no topo, com foco, e o 422 anterior sai (crítica G3). */
  startReject(): void {
    this.topError.set(null);
    this.showRejectReason.set(true);
    setTimeout(() => {
      const panel = this.rejectPanel?.nativeElement;
      panel?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      panel?.querySelector<HTMLTextAreaElement>('textarea')?.focus();
    });
  }

  cancelReject(): void {
    this.showRejectReason.set(false);
    this.rejectReason.setValue(null);
  }

  /** "Rejeitar resposta": o modo rejeitar já é a confirmação, sem um segundo diálogo (crítica G5). */
  confirmReject(): void {
    const proposal = this.proposal();
    if (!proposal || !this.isPending() || this.rejecting() || this.submitting()) return;
    this.reject(proposal);
  }

  /** "Ver e-mail completo": abre o histórico de e-mails do RFQ já na resposta do banco. */
  openEmail(): void {
    const proposal = this.proposal();
    if (!proposal || this.openingEmail()) return;
    this.openingEmail.set(true);
    this.rfqHttp
      .getRfqById(proposal.rfqId)
      .pipe(finalize(() => this.openingEmail.set(false)))
      .subscribe({
        next: rfq => {
          if (rfq) this.modalEmails?.open(rfq, proposal.rfqEmailId);
          else
            this.toast.showToastWarn(
              'E-mail indisponível',
              'Não encontramos o RFQ desta resposta. Abra pela tela de RFQs.'
            );
        },
        error: () =>
          this.toast.showToastWarn(
            'E-mail indisponível',
            'Não conseguimos abrir o e-mail agora. Tente de novo ou abra pela tela de RFQs.'
          ),
      });
  }

  close(): void {
    this.dialogRef?.close();
  }

  private reject(proposal: BankLimitProposalResponse): void {
    this.rejecting.set(true);
    this.topError.set(null);
    const reason = this.rejectReason.value?.trim() || null;
    this.http
      .reject(proposal.id, { reason: reason ? reason.slice(0, 500) : null })
      .pipe(finalize(() => this.rejecting.set(false)))
      .subscribe({
        next: updated => {
          this.toast.showToastSuccess(
            'Resposta rejeitada',
            `Resposta do ${proposal.bankName} rejeitada. O limite cadastrado não mudou.`
          );
          this.finish(updated ?? proposal);
        },
        error: error =>
          this.topError.set(
            bankLimitUpdateErrorMessage(
              error,
              'Não conseguimos rejeitar agora. Confira a conexão e tente de novo.'
            )
          ),
      });
  }

  private finish(proposal: BankLimitProposalResponse): void {
    this.decided.emit(proposal);
    this.close();
  }

  private applyProposal(proposal: BankLimitProposalResponse): void {
    this.proposal.set(proposal);
    this.topError.set(null);
    this.linesArray.clear({ emitEvent: false });
    for (const line of proposal.lines) {
      const value = initialLineValue(line);
      this.linesArray.push(
        this.fb.group({
          apply: this.fb.control(value.apply),
          amountBrl: this.fb.control<number | null>(value.amountBrl),
          validityStartDate: this.fb.control<string | null>(value.validityStartDate),
          validityEndDate: this.fb.control<string | null>(value.validityEndDate),
        }),
        { emitEvent: false }
      );
    }
    this.linesArray.updateValueAndValidity();
  }

  /** Lê o formulário normalizando as datas do `app-calendar` (Date ou texto) para `YYYY-MM-DD`. */
  private readLines(): ProposalLineFormValue[] {
    const lines = this.proposal()?.lines ?? [];
    return this.linesArray.controls.map((group, index) => {
      const raw = group.getRawValue();
      return {
        limitTypeCode: lines[index]?.limitTypeCode ?? 'Loan4131',
        apply: raw.apply,
        amountBrl:
          raw.amountBrl === null || (raw.amountBrl as unknown) === ''
            ? null
            : Number(raw.amountBrl),
        validityStartDate: this.toIsoDate(raw.validityStartDate),
        validityEndDate: this.toIsoDate(raw.validityEndDate),
      };
    });
  }

  private toIsoDate(value: string | Date | null): string | null {
    if (!value) return null;
    return this.dateFormatter.formatDateToYYYYMMDD(value) || null;
  }
}
